import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  ArrowUpRight,
  BarChart3,
  BrainCircuit,
  CheckCircle2,
  CheckSquare,
  ChevronDown,
  ChevronRight as ChevronRightIcon,
  ChevronUp,
  Clock,
  Database,
  FileText,
  HardDrive,
  Headset,
  Megaphone,
  Network as NetworkIcon,
  Package,
  Search,
  Server,
  Settings as SettingsIcon,
  ShieldAlert,
  ShoppingBag,
  Sparkles,
  Square,
  Store,
  Tags,
  TrendingUp,
  Truck,
  Users,
  Wallet,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  PolarAngleAxis,
  RadialBar,
  RadialBarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type {
  LifecycleEvent,
  NotificationChannel,
  NotificationLog,
  NotificationTemplate,
  PayoutRequest,
  Settlement,
  SettlementRunSummary,
  VendorListItem,
} from '@terra/api-client';
import {
  Badge,
  type BadgeTone,
  Card,
  CardBody,
  EmptyState,
  PageHeader,
  Spinner,
  StatusPill,
  Button,
  Input,
} from '@terra/ui';
import { formatPriceShort } from '@terra/utils';

import { AsyncUserSelect, AsyncVendorSelect } from '../components/AsyncSelect';
import { FinanceOverview } from '../components/FinanceOverview';
import { FinancePageHeader, FinancePanelHeader } from '../components/FinancePageHeader';
import { WalletLedgerTable } from '../components/WalletLedgerTable';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';

const formatPaise = (p: number) =>
  `₹${(p / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
/** Indian dashboard figure: ₹2,435 · ₹1.25 L · ₹2.5 Cr (never "K"). */
const compactInr = (p: number) => formatPriceShort(p || 0);

// ══════════════════════════════════════════════════════════════════
// DASHBOARD  — premium analytics overview
// ══════════════════════════════════════════════════════════════════

// ── Motion presets ───────────────────────────────────────────────────────────

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.5, ease: [0.16, 1, 0.3, 1] as const },
  }),
};

function Skeleton({ className }: { className?: string }) {
  return <div className={`bg-surface-subtle animate-pulse rounded-md ${className}`} />;
}

interface ChannelDatum {
  label: string;
  value: number;
  color: string;
}

export function DashboardPage() {
  const user = useAuthStore((s) => s.user);

  const statsQ = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => api.vendors.dashboardStats(),
  });
  const dashQ = useQuery({
    queryKey: ['analytics-dashboard'],
    queryFn: () => api.analytics.dashboard(),
    retry: false,
  });

  const stats = statsQ.data;
  const dash = dashQ.data;
  const loading = statsQ.isLoading;

  const kpis: {
    label: string;
    value: number;
    Icon: LucideIcon;
    to: string;
    cta: string;
    accent: 'gold' | 'red' | 'accent' | 'neutral';
    alert: boolean;
  }[] = [
    {
      label: 'Vendors pending approval',
      value: stats?.pendingVendors ?? 0,
      Icon: Store,
      to: '/sellers?tab=compliance',
      cta: 'Review queue',
      accent: 'red',
      alert: true,
    },
    {
      label: 'Products awaiting review',
      value: stats?.pendingProducts ?? 0,
      Icon: Package,
      to: '/products',
      cta: 'Moderate catalog',
      accent: 'gold',
      alert: true,
    },
    {
      label: 'Orders today',
      value: stats?.todayOrders ?? 0,
      Icon: ShoppingBag,
      to: '/orders/all',
      cta: 'Track fulfilment',
      accent: 'accent',
      alert: false,
    },
    {
      label: 'Payouts to release',
      value: stats?.pendingPayouts ?? 0,
      Icon: Wallet,
      to: '/payouts',
      cta: 'Open settlements',
      accent: 'neutral',
      alert: false,
    },
  ];

  const channels: ChannelDatum[] = [
    { label: 'Marketplace', value: dash?.platform?.marketplaceSalesPaise ?? 0, color: '#D9A93A' },
    { label: 'POS', value: dash?.platform?.posSalesPaise ?? 0, color: '#D63232' },
    { label: 'B2B', value: dash?.platform?.b2bSalesPaise ?? 0, color: '#4A453E' },
  ];
  const channelTotal = channels.reduce((s, c) => s + c.value, 0);

  const fin = dash?.finance;
  const finRows = fin
    ? [
        { label: 'Revenue', value: fin.revenuePaise, color: '#D9A93A' },
        { label: 'Cost of goods', value: fin.cogsPaise, color: '#CFCFCF' },
        { label: 'Operating expense', value: fin.operatingExpensePaise, color: '#A6A6A6' },
        {
          label: 'Net',
          value: Math.abs(fin.netPaise),
          color: fin.netPaise >= 0 ? '#8F6A20' : '#D63232',
        },
      ]
    : [];
  const finMax = Math.max(1, ...finRows.map((r) => r.value));

  const slaPct = (dash?.ops?.slaBps ?? 0) / 100;

  const quickLinks: { to: string; label: string; Icon: LucideIcon }[] = [
    { to: '/analytics', label: 'Analytics', Icon: BarChart3 },
    { to: '/crm', label: 'Crm', Icon: Users },
    { to: '/wallet', label: 'Wallet', Icon: Wallet },
    { to: '/intelligence', label: 'Intelligence', Icon: BrainCircuit },
    ...(import.meta.env.VITE_FEATURE_MLM_ENABLED === 'true'
      ? [{ to: '/network', label: 'Network', Icon: NetworkIcon }]
      : []),
    { to: '/settings', label: 'Settings', Icon: SettingsIcon },
  ];

  return (
    <div className="space-y-5 pb-6">
      {/* ── KPI row ──────────────────────────────────────────────────── */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {kpis.map((k, i) => {
          const ringTone =
            k.accent === 'red'
              ? 'bg-gradient-to-br from-red-50 to-red-100 text-red-600 ring-red-200 group-hover:from-red-500 group-hover:to-red-600 group-hover:text-white group-hover:ring-red-500 group-hover:shadow-[0_0_15px_rgba(220,38,38,0.3)]'
              : k.accent === 'accent'
                ? 'bg-gradient-to-br from-accent-50 to-accent-100 text-accent-600 ring-accent-200 group-hover:from-accent-400 group-hover:to-accent-600 group-hover:text-white group-hover:ring-accent-500 group-hover:shadow-[0_0_15px_rgba(79,70,229,0.3)]'
                : k.accent === 'neutral'
                  ? 'bg-gradient-to-br from-surface-subtle to-surface-subtle/50 text-ink-muted ring-border/50 group-hover:from-ink group-hover:to-ink/90 group-hover:text-white group-hover:ring-ink group-hover:shadow-[0_0_15px_rgba(0,0,0,0.1)]'
                  : 'bg-gradient-to-br from-gold-50 to-gold-100 text-gold-700 ring-gold-200 group-hover:from-gold-400 group-hover:to-gold-500 group-hover:text-zone-900 group-hover:ring-gold-500 group-hover:shadow-[0_0_15px_rgba(217,169,58,0.3)]';

          return (
            <motion.div key={k.label} custom={i} variants={fadeUp} initial="hidden" animate="show">
              <Link
                to={k.to}
                className="bg-surface hover:ring-gold-300/50 group relative flex h-full flex-col overflow-hidden rounded-3xl border border-transparent p-6 shadow-[0_8px_30px_-4px_rgba(0,0,0,0.04)] ring-1 ring-transparent transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_40px_-4px_rgba(0,0,0,0.08)]"
              >
                {/* Subtle highlight gradient on hover */}
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/40 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 dark:from-white/5" />

                <div className="relative flex items-center justify-between">
                  <span
                    className={`grid h-10 w-10 place-items-center rounded-xl ring-1 ring-inset transition-all duration-300 ${ringTone}`}
                  >
                    <k.Icon className="h-[18px] w-[18px]" strokeWidth={2} />
                  </span>
                  {k.alert && Number(k.value) > 0 ? (
                    <span className="flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-1 text-[11px] font-bold tracking-wide text-red-600 ring-1 ring-inset ring-red-100">
                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" />
                      Action
                    </span>
                  ) : (
                    <span className="text-ink-subtle flex items-center gap-1.5 text-[11px] font-semibold">
                      <span className="bg-gold-400 h-1.5 w-1.5 rounded-full" /> Live
                    </span>
                  )}
                </div>

                <div className="relative mt-5 flex-1">
                  <div className="text-ink text-[32px] font-extrabold leading-none tracking-tight">
                    {loading ? <Skeleton className="h-[32px] w-20" /> : k.value}
                  </div>
                  <p className="text-ink-muted mt-1.5 text-[13px] font-medium">{k.label}</p>
                </div>

                <div className="border-border/60 text-gold-700 group-hover:text-gold-600 relative mt-4 flex items-center gap-1.5 border-t pt-3.5 text-[12px] font-bold transition-colors duration-300">
                  {k.cta}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </div>
              </Link>
            </motion.div>
          );
        })}
      </div>

      {/* ── Sales by channel + delivery ──────────────────────────────── */}
      <motion.div
        custom={4}
        variants={fadeUp}
        initial="hidden"
        animate="show"
        className="grid gap-4 lg:grid-cols-3"
      >
        {/* Channel mix */}
        <div className="bg-surface flex flex-col overflow-hidden rounded-3xl border border-transparent p-7 shadow-[0_8px_30px_-4px_rgba(0,0,0,0.04)] lg:col-span-2">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-ink text-[16px] font-bold tracking-tight">Sales by channel</h2>
              <p className="text-ink-muted mt-1 text-[13px] font-medium">
                Gross merchandise value across all storefronts
              </p>
            </div>
            <Link
              to="/analytics"
              className="border-border/60 bg-surface-subtle/50 text-ink-muted hover:border-gold-300 hover:bg-gold-50 hover:text-gold-700 hidden items-center gap-1.5 rounded-lg border px-4 py-2 text-[13px] font-semibold transition-all duration-300 hover:shadow-sm sm:flex"
            >
              Details <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          <div className="mt-6 flex flex-col items-center gap-6 sm:flex-row sm:gap-8">
            <div className="relative h-[180px] w-[180px] shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Tooltip
                    cursor={{ fill: 'transparent' }}
                    contentStyle={{
                      borderRadius: '12px',
                      border: 'none',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    }}
                    itemStyle={{ fontSize: '13px', fontWeight: 600 }}
                    formatter={(value: number) => compactInr(value)}
                  />
                  <Pie
                    data={
                      channelTotal > 0 ? channels : [{ label: 'none', value: 1, color: '#F2F2F2' }]
                    }
                    dataKey="value"
                    nameKey="label"
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={88}
                    paddingAngle={channelTotal > 0 ? 4 : 0}
                    stroke="none"
                    startAngle={90}
                    endAngle={-270}
                  >
                    {(channelTotal > 0
                      ? channels
                      : [{ label: 'none', value: 1, color: '#F2F2F2' }]
                    ).map((c) => (
                      <Cell
                        key={c.label}
                        fill={c.color}
                        className="transition-all duration-300 hover:opacity-80"
                      />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 grid place-items-center text-center">
                <div>
                  <p className="text-ink-subtle text-[10px] font-bold uppercase tracking-wider">
                    Total gmv
                  </p>
                  <div className="text-ink mt-0.5 text-[22px] font-extrabold tracking-tight">
                    {loading ? (
                      <Skeleton className="mx-auto h-[22px] w-20" />
                    ) : dash?.platform ? (
                      compactInr(dash.platform.gmvPaise)
                    ) : (
                      '—'
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="w-full flex-1 space-y-3">
              {channels.map((c) => {
                const pctNum = channelTotal > 0 ? (c.value / channelTotal) * 100 : 0;
                return (
                  <div
                    key={c.label}
                    className="border-border/40 hover:bg-surface-subtle group rounded-xl border p-3 transition-all duration-300 hover:border-transparent hover:shadow-sm"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-ink flex items-center gap-2.5 text-[13px] font-semibold">
                        <span
                          className="h-2.5 w-2.5 rounded-full transition-transform duration-300 group-hover:scale-125"
                          style={{ backgroundColor: c.color }}
                        />
                        {c.label}
                      </span>
                      <span className="text-ink text-[13px] font-bold">
                        {loading ? (
                          <Skeleton className="h-4 w-14" />
                        ) : dash ? (
                          compactInr(c.value)
                        ) : (
                          '—'
                        )}
                      </span>
                    </div>
                    <div className="bg-surface-subtle/80 ring-border/20 mt-2.5 h-1.5 overflow-hidden rounded-full ring-1 ring-inset">
                      <div
                        className="h-full rounded-full transition-all duration-1000 ease-out"
                        style={{ width: `${pctNum}%`, backgroundColor: c.color }}
                      />
                    </div>
                    <p className="text-ink-subtle mt-1.5 text-[11px] font-medium">
                      {pctNum.toFixed(1)}% of gmv
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="border-border/60 mt-5 grid grid-cols-2 gap-4 border-t pt-4">
            <div className="border-border/40 bg-surface-subtle/30 rounded-xl border p-3 text-center">
              <p className="text-ink-subtle text-[11px] font-medium">Total orders</p>
              <div className="text-ink mt-0.5 text-[18px] font-extrabold">
                {loading ? (
                  <Skeleton className="mx-auto h-6 w-10" />
                ) : (
                  (dash?.platform?.orderCount ?? '—')
                )}
              </div>
            </div>
            <div className="border-border/40 bg-surface-subtle/30 rounded-xl border p-3 text-center">
              <p className="text-ink-subtle text-[11px] font-medium">Unique customers</p>
              <div className="text-ink mt-0.5 text-[18px] font-extrabold">
                {loading ? (
                  <Skeleton className="mx-auto h-6 w-10" />
                ) : (
                  (dash?.platform?.uniqueCustomerCount ?? '—')
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Delivery performance */}
        <div className="bg-surface flex flex-col rounded-3xl border border-transparent p-7 shadow-[0_8px_30px_-4px_rgba(0,0,0,0.04)]">
          <h2 className="text-ink text-[16px] font-bold tracking-tight">Delivery performance</h2>
          <p className="text-ink-muted mt-0.5 text-[13px] font-medium">On-time fulfilment rate</p>

          <div className="relative mx-auto my-5 h-[160px] w-[160px]">
            <ResponsiveContainer width="100%" height="100%">
              <RadialBarChart
                innerRadius="76%"
                outerRadius="100%"
                data={[{ name: 'sla', value: loading ? 0 : slaPct }]}
                startAngle={90}
                endAngle={-270}
              >
                <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
                <RadialBar
                  background={{ fill: '#F2F2F2' }}
                  dataKey="value"
                  cornerRadius={20}
                  fill="#D9A93A"
                  angleAxisId={0}
                  className="transition-all duration-1000"
                />
              </RadialBarChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 grid place-items-center text-center">
              <div>
                <div className="text-ink text-[28px] font-extrabold tracking-tight">
                  {loading ? (
                    <Skeleton className="mx-auto h-[28px] w-14" />
                  ) : (
                    `${Math.round(slaPct)}%`
                  )}
                </div>
                <p className="text-ink-subtle mt-0.5 text-[10px] font-bold uppercase tracking-[0.1em]">
                  Sla
                </p>
              </div>
            </div>
          </div>

          <div className="mt-auto space-y-2.5">
            <div className="border-border/40 bg-surface-subtle/50 hover:bg-surface-subtle flex items-center justify-between rounded-xl border px-3.5 py-3 transition-colors">
              <span className="text-ink-muted flex items-center gap-2 text-[13px] font-semibold">
                <Truck className="text-gold-600 h-3.5 w-3.5" /> Delivered
              </span>
              <span className="text-ink text-[13px] font-bold">
                {loading ? <Skeleton className="h-4 w-6" /> : (dash?.ops?.deliveredCount ?? '—')}
              </span>
            </div>
            <div className="border-border/40 bg-surface-subtle/50 hover:bg-surface-subtle flex items-center justify-between rounded-xl border px-3.5 py-3 transition-colors">
              <span className="text-ink-muted flex items-center gap-2 text-[13px] font-semibold">
                <Clock className="text-gold-600 h-3.5 w-3.5" /> On-time
              </span>
              <span className="text-ink text-[13px] font-bold">
                {loading ? <Skeleton className="h-4 w-6" /> : (dash?.ops?.onTimeCount ?? '—')}
              </span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* ── Financial breakdown ──────────────────────────────────────── */}
      <motion.div
        custom={5}
        variants={fadeUp}
        initial="hidden"
        animate="show"
        className="bg-surface rounded-3xl border border-transparent p-7 shadow-[0_8px_30px_-4px_rgba(0,0,0,0.04)]"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-ink text-[16px] font-bold tracking-tight">Financial breakdown</h2>
            <p className="text-ink-muted mt-0.5 text-[13px] font-medium">
              Revenue, cost and margin for the current period
            </p>
          </div>
          {fin && !loading && (
            <div className="flex gap-2">
              <span className="bg-gold-50 text-gold-700 ring-gold-100 rounded-lg px-2.5 py-1.5 text-[11px] font-bold ring-1 ring-inset">
                Gross {(fin.grossMarginBps / 100).toFixed(1)}%
              </span>
              <span
                className={`rounded-lg px-2.5 py-1.5 text-[11px] font-bold ring-1 ring-inset ${fin.netMarginBps >= 0 ? 'bg-gold-50 text-gold-700 ring-gold-100' : 'bg-red-50 text-red-600 ring-red-100'}`}
              >
                Net {(fin.netMarginBps / 100).toFixed(1)}%
              </span>
            </div>
          )}
        </div>

        {loading ? (
          <div className="mt-5 space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i}>
                <div className="flex justify-between">
                  <Skeleton className="h-3.5 w-28" />
                  <Skeleton className="h-3.5 w-16" />
                </div>
                <Skeleton className="mt-2 h-2.5 w-full" />
              </div>
            ))}
          </div>
        ) : fin ? (
          <div className="mt-5 space-y-4">
            {finRows.map((r) => (
              <div key={r.label} className="group">
                <div className="flex items-center justify-between text-[13px]">
                  <span className="text-ink-muted group-hover:text-ink font-semibold transition-colors">
                    {r.label}
                  </span>
                  <span className="text-ink font-bold">{formatPaise(r.value)}</span>
                </div>
                <div className="bg-surface-subtle ring-border/20 relative mt-2 h-2 overflow-hidden rounded-full ring-1 ring-inset">
                  <div
                    className="h-full rounded-full transition-all duration-1000 ease-out"
                    style={{ width: `${(r.value / finMax) * 100}%`, backgroundColor: r.color }}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="border-border/60 bg-surface-subtle/30 mt-5 flex flex-col items-center justify-center rounded-2xl border border-dashed py-10 text-center">
            <span className="bg-surface-subtle text-ink-muted mb-2 rounded-full p-2.5">
              <Wallet className="h-5 w-5" strokeWidth={1.5} />
            </span>
            <p className="text-ink text-[14px] font-bold">No analytics data yet</p>
            <p className="text-ink-muted mt-0.5 text-[12px] font-medium">
              Run a snapshot to populate financial metrics.
            </p>
            <Link
              to="/analytics"
              className="bg-gold-500 text-zone-900 hover:bg-gold-400 hover:shadow-glow-gold group mt-4 inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-[12px] font-bold shadow-sm transition-all duration-300 hover:-translate-y-0.5"
            >
              Go to analytics{' '}
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        )}
      </motion.div>

      {/* ── Quick links ──────────────────────────────────────────────── */}
      <motion.div
        custom={6}
        variants={fadeUp}
        initial="hidden"
        animate="show"
        className="bg-surface rounded-3xl border border-transparent p-7 shadow-[0_8px_30px_-4px_rgba(0,0,0,0.04)]"
      >
        <h2 className="text-ink text-[16px] font-bold tracking-tight">Jump to a workspace</h2>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {quickLinks.map((q) => (
            <Link
              key={q.to}
              to={q.to}
              className="border-border/50 bg-surface hover:shadow-glow-accent group relative flex flex-col items-center gap-2 overflow-hidden rounded-xl border p-4 text-center transition-all duration-300 hover:-translate-y-0.5 hover:border-transparent"
            >
              <div className="from-surface-subtle/50 pointer-events-none absolute inset-0 bg-gradient-to-b to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

              <span className="bg-surface-subtle text-ink-muted group-hover:bg-gold-500 group-hover:text-zone-900 relative grid h-10 w-10 place-items-center rounded-xl transition-all duration-300 group-hover:scale-110 group-hover:shadow-md">
                <q.Icon className="h-[18px] w-[18px]" strokeWidth={2} />
              </span>
              <span className="text-ink group-hover:text-gold-700 relative text-[12px] font-bold transition-colors">
                {q.label}
              </span>
            </Link>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// PRODUCT APPROVALS
// ══════════════════════════════════════════════════════════════════

const PRODUCT_STATUS_TONE: Record<string, BadgeTone> = {
  DRAFT: 'neutral',
  PENDING_APPROVAL: 'warning',
  CHANGES_REQUESTED: 'warning',
  APPROVED: 'success',
  REJECTED: 'danger',
  ARCHIVED: 'neutral',
};

const REVIEW_CHECKLIST = [
  { id: 'images', label: 'Images are clear, accurate, and meet quality standards' },
  { id: 'title', label: 'Title and description are appropriate and not misleading' },
  { id: 'price', label: 'Price and MRP are reasonable and correctly set' },
  { id: 'hsn', label: 'HSN/SAC code and GST rate are correct for this product type' },
  { id: 'policy', label: 'No prohibited items, counterfeit goods, or policy violations' },
] as const;

type ChecklistId = (typeof REVIEW_CHECKLIST)[number]['id'];

function ProductReviewDrawer({
  product,
  onClose,
  onSuccess,
}: {
  product: any;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [checked, setChecked] = useState<Record<ChecklistId, boolean>>(
    Object.fromEntries(REVIEW_CHECKLIST.map((c) => [c.id, false])) as Record<ChecklistId, boolean>,
  );
  const [note, setNote] = useState('');
  // The photo shown large; the checklist asks about images, so they are here.
  const [preview, setPreview] = useState<string | null>(null);
  const media: Array<{ id: string; url: string; isPrimary?: boolean }> = [
    ...(product.media ?? []),
  ].sort(
    (a: { isPrimary?: boolean }, b: { isPrimary?: boolean }) =>
      Number(!!b.isPrimary) - Number(!!a.isPrimary),
  );
  const variants: Array<{
    id: string;
    sku: string;
    pricePaise: number;
    mrpPaise: number;
    optionValues?: Record<string, string>;
  }> = product.variants ?? [];
  const specs = Object.entries((product.attributes ?? {}) as Record<string, unknown>).filter(
    ([, v]) => typeof v === 'string' || typeof v === 'number',
  );

  const allChecked = REVIEW_CHECKLIST.every((c) => checked[c.id]);
  const noteValid = note.trim().length >= 10;

  const approve = useMutation({
    mutationFn: () => api.products.approve(product.id),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
  });
  const reject = useMutation({
    mutationFn: () => api.products.reject(product.id, { note: note.trim() }),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
  });
  const requestChanges = useMutation({
    mutationFn: () => api.products.requestChanges(product.id, { note: note.trim() }),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
  });

  const isPending = approve.isPending || reject.isPending || requestChanges.isPending;
  const formatPaiseLocal = (p: number) =>
    `₹${(p / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {preview && (
        <div
          className="fixed inset-0 z-[60] grid place-items-center bg-black/80 p-6"
          onClick={() => setPreview(null)}
          role="dialog"
          aria-label="Product image"
        >
          <img src={preview} alt="" className="max-h-full max-w-full rounded-lg object-contain" />
          <button
            type="button"
            className="absolute right-5 top-5 rounded-full bg-white/90 p-2 text-black"
            onClick={() => setPreview(null)}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Drawer */}
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
        className="bg-surface border-border/60 relative ml-auto flex h-full w-full max-w-xl flex-col overflow-hidden border-l shadow-2xl"
      >
        {/* Header */}
        <div className="border-border/60 bg-surface-subtle/30 flex items-start justify-between gap-4 border-b px-6 py-5">
          <div className="min-w-0 flex-1">
            <p className="text-ink-muted mb-1 text-[11px] font-bold uppercase tracking-wider">
              Product Review
            </p>
            <h2 className="text-ink truncate text-[16px] font-extrabold leading-tight">
              {product.title}
            </h2>
            <p className="text-ink-muted mt-0.5 text-[13px] font-medium">
              {product.vendor?.storeName ?? '—'} · {product.category?.name ?? '—'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-ink-muted hover:text-ink hover:bg-surface-subtle mt-0.5 shrink-0 rounded-lg p-1.5 transition-colors"
          >
            <X className="h-5 w-5" strokeWidth={2} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">
          {/* Product snapshot */}
          <div className="border-border/60 grid grid-cols-3 gap-0 border-b">
            {[
              { label: 'SKU', value: product.variants?.[0]?.sku ?? '—' },
              {
                label: 'Price',
                value: product.variants?.[0]
                  ? formatPaiseLocal(product.variants[0].pricePaise)
                  : '—',
              },
              { label: 'Images', value: `${product.media?.length ?? 0} uploaded` },
            ].map((item) => (
              <div key={item.label} className="border-border/40 border-r px-5 py-4 last:border-r-0">
                <p className="text-ink-muted text-[11px] font-bold uppercase tracking-wider">
                  {item.label}
                </p>
                <p className="text-ink mt-1 text-[14px] font-bold">{item.value}</p>
              </div>
            ))}
          </div>

          {/* What the checklist is about: photos, copy, prices, tax. */}
          <div className="border-border/60 space-y-5 border-b px-6 py-5">
            <section>
              <h3 className="text-ink-muted mb-2 text-[11px] font-bold uppercase tracking-wider">
                Images ({media.length})
              </h3>
              {media.length === 0 ? (
                <p className="rounded-xl border border-dashed border-red-200 bg-red-50 px-3 py-4 text-center text-xs font-semibold text-red-700">
                  No images uploaded.
                </p>
              ) : (
                <div className="grid grid-cols-4 gap-2">
                  {media.map((m, i) => (
                    <button
                      key={m.id ?? m.url}
                      type="button"
                      onClick={() => setPreview(m.url)}
                      className="border-border/60 hover:border-gold-400 relative aspect-square overflow-hidden rounded-lg border bg-white"
                      title="View large"
                    >
                      <img
                        src={m.url}
                        alt=""
                        className="h-full w-full object-contain"
                        loading="lazy"
                      />
                      {i === 0 && (
                        <span className="absolute left-1 top-1 rounded bg-black/60 px-1.5 py-0.5 text-[9px] font-bold text-white">
                          COVER
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </section>

            <section>
              <h3 className="text-ink-muted mb-1 text-[11px] font-bold uppercase tracking-wider">
                Description
              </h3>
              {product.description?.trim() ? (
                <p className="text-ink whitespace-pre-line text-[13px] leading-relaxed">
                  {product.description}
                </p>
              ) : (
                <p className="text-[13px] font-semibold text-red-600">No description.</p>
              )}
              {(product.bulletPoints ?? []).length > 0 && (
                <ul className="text-ink mt-2 list-disc space-y-0.5 pl-5 text-[13px]">
                  {(product.bulletPoints as string[]).map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
              )}
            </section>

            <section>
              <h3 className="text-ink-muted mb-1 text-[11px] font-bold uppercase tracking-wider">
                Variants and prices
              </h3>
              <div className="border-border/60 overflow-hidden rounded-lg border">
                <table className="w-full text-left text-[12px]">
                  <thead className="bg-surface-subtle/50 text-ink-muted">
                    <tr>
                      <th className="px-3 py-1.5 font-semibold">Variant</th>
                      <th className="px-3 py-1.5 font-semibold">SKU</th>
                      <th className="px-3 py-1.5 text-right font-semibold">MRP</th>
                      <th className="px-3 py-1.5 text-right font-semibold">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {variants.map((v) => {
                      const opts = Object.values(v.optionValues ?? {}).join(' / ');
                      const off =
                        v.mrpPaise > 0 ? Math.round((1 - v.pricePaise / v.mrpPaise) * 100) : 0;
                      return (
                        <tr key={v.id} className="border-border/40 border-t">
                          <td className="text-ink px-3 py-1.5">{opts || 'Default'}</td>
                          <td className="text-ink-muted px-3 py-1.5 font-mono">{v.sku}</td>
                          <td className="text-ink-muted px-3 py-1.5 text-right">
                            {formatPaiseLocal(v.mrpPaise)}
                          </td>
                          <td className="text-ink px-3 py-1.5 text-right font-semibold">
                            {formatPaiseLocal(v.pricePaise)}
                            {off > 0 && <span className="ml-1 text-emerald-700">{off}% off</span>}
                            {v.pricePaise > v.mrpPaise && (
                              <span className="ml-1 text-red-600">above MRP</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="grid grid-cols-3 gap-3 text-[12px]">
              <div>
                <p className="text-ink-muted text-[11px] font-bold uppercase tracking-wider">
                  HSN/SAC
                </p>
                <p className="text-ink font-mono font-semibold">{product.hsnSac ?? '—'}</p>
              </div>
              <div>
                <p className="text-ink-muted text-[11px] font-bold uppercase tracking-wider">GST</p>
                <p className="text-ink font-semibold">
                  {product.gstRate != null ? `${product.gstRate}%` : '—'}
                </p>
              </div>
              <div>
                <p className="text-ink-muted text-[11px] font-bold uppercase tracking-wider">
                  Brand
                </p>
                <p className="text-ink font-semibold">
                  {product.brandRef?.name ?? product.brand ?? 'Unbranded'}
                </p>
              </div>
            </section>

            {specs.length > 0 && (
              <section>
                <h3 className="text-ink-muted mb-1 text-[11px] font-bold uppercase tracking-wider">
                  Specifications
                </h3>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-[12px]">
                  {specs.map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-2">
                      <dt className="text-ink-muted truncate">{k}</dt>
                      <dd className="text-ink truncate font-medium">{String(v)}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            )}
          </div>

          {/* Review checklist */}
          <div className="px-6 py-5">
            <div className="mb-4 flex items-center gap-2">
              <div className="bg-gold-50 text-gold-700 grid h-7 w-7 place-items-center rounded-lg">
                <CheckSquare className="h-4 w-4" strokeWidth={2} />
              </div>
              <h3 className="text-ink text-[14px] font-bold">Review checklist</h3>
              <span className="text-ink-muted ml-auto text-[12px] font-semibold">
                {REVIEW_CHECKLIST.filter((c) => checked[c.id]).length}/{REVIEW_CHECKLIST.length}{' '}
                done
              </span>
            </div>
            <div className="space-y-2">
              {REVIEW_CHECKLIST.map((item) => {
                const isChecked = checked[item.id];
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setChecked((prev) => ({ ...prev, [item.id]: !prev[item.id] }))}
                    className={`flex w-full items-start gap-3 rounded-xl border px-4 py-3 text-left transition-all duration-200 ${
                      isChecked
                        ? 'border-green-200 bg-green-50 text-green-800'
                        : 'border-border/60 bg-surface-subtle/40 text-ink hover:border-border hover:bg-surface-subtle'
                    }`}
                  >
                    {isChecked ? (
                      <CheckSquare
                        className="mt-0.5 h-4 w-4 shrink-0 text-green-600"
                        strokeWidth={2.5}
                      />
                    ) : (
                      <Square className="text-ink-muted mt-0.5 h-4 w-4 shrink-0" strokeWidth={2} />
                    )}
                    <span className="text-[13px] font-semibold leading-snug">{item.label}</span>
                  </button>
                );
              })}
            </div>

            {!allChecked && (
              <p className="text-ink-muted mt-3 text-[12px] font-medium">
                ✦ All items must be checked before you can approve this product.
              </p>
            )}
          </div>

          {/* Review note */}
          <div className="border-border/60 border-t px-6 py-5">
            <label className="text-ink mb-2 block text-[13px] font-bold">
              Review note
              <span className="text-ink-muted ml-1 font-normal">
                (required for Reject &amp; Request Changes)
              </span>
            </label>
            <textarea
              rows={4}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Write specific feedback for the vendor — what needs to change, why the product was rejected, or any approval conditions…"
              className="border-border/60 bg-surface-subtle/40 text-ink placeholder:text-ink-subtle focus:border-ink focus:ring-ink/20 w-full resize-none rounded-xl border px-4 py-3 text-[13px] font-medium outline-none transition-all focus:bg-white focus:ring-2"
            />
            {note.trim().length > 0 && note.trim().length < 10 && (
              <p className="mt-1.5 text-[12px] font-semibold text-red-500">
                Note must be at least 10 characters.
              </p>
            )}
          </div>
        </div>

        {/* Footer actions */}
        <div className="border-border/60 bg-surface-subtle/20 flex items-center gap-3 border-t px-6 py-4">
          <button
            type="button"
            disabled={!noteValid || isPending}
            onClick={() => reject.mutate()}
            className="flex h-10 items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 text-[13px] font-bold text-red-700 transition-all hover:border-red-300 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Reject
          </button>
          <button
            type="button"
            disabled={!noteValid || isPending}
            onClick={() => requestChanges.mutate()}
            className="border-border/60 bg-surface flex h-10 items-center gap-2 rounded-xl border px-4 text-[13px] font-bold transition-all hover:border-amber-300 hover:bg-amber-50 hover:text-amber-800 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Request Changes
          </button>
          <button
            type="button"
            disabled={!allChecked || isPending}
            onClick={() => approve.mutate()}
            className="ml-auto flex h-10 items-center gap-2 rounded-xl bg-green-600 px-5 text-[13px] font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-green-500 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0"
          >
            {approve.isPending ? 'Approving…' : '✓ Approve'}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

export function ProductApprovalsPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('PENDING_APPROVAL');
  const [search, setSearch] = useState('');
  const [reviewing, setReviewing] = useState<any | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-products', statusFilter, search],
    queryFn: () => {
      const opts: Record<string, unknown> = { pageSize: 50 };
      if (statusFilter) opts.status = statusFilter;
      if (search) opts.q = search;
      return api.products.listAll(opts);
    },
  });

  function onReviewSuccess() {
    void qc.invalidateQueries({ queryKey: ['admin-products'] });
    void qc.invalidateQueries({ queryKey: ['dashboard-stats'] });
  }

  const products = data?.data ?? [];
  const pendingCount = products.filter((p: any) => p.status === 'PENDING_APPROVAL').length;

  return (
    <div>
      <PageHeader
        title="Product approvals"
        description="Review vendor submissions using the structured checklist before approving or rejecting."
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex gap-1">
          {['PENDING_APPROVAL', 'APPROVED', 'REJECTED', 'CHANGES_REQUESTED', ''].map((s) => (
            <Button
              key={s}
              size="sm"
              variant={statusFilter === s ? 'primary' : 'outline'}
              onClick={() => setStatusFilter(s)}
            >
              {s ? s.replace(/_/g, ' ') : 'All'}
              {s === 'PENDING_APPROVAL' && pendingCount > 0 && (
                <span className="ml-1.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-black text-white">
                  {pendingCount}
                </span>
              )}
            </Button>
          ))}
        </div>
        <Input
          placeholder="Search products…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-60"
        />
      </div>

      {isLoading ? (
        <div className="grid min-h-[200px] place-items-center">
          <Spinner size="lg" />
        </div>
      ) : products.length === 0 ? (
        <EmptyState title="No products" description="No products match the current filter." />
      ) : (
        <div className="border-border bg-surface overflow-x-auto rounded-lg border">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-border bg-surface-subtle text-ink-muted border-b text-xs font-medium uppercase">
                <th className="px-4 py-3">Product</th>
                <th className="px-4 py-3">Vendor</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Variants</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p: any) => (
                <tr
                  key={p.id}
                  className="border-border hover:bg-surface-subtle/50 border-b last:border-0"
                >
                  <td className="px-4 py-3">
                    <div className="font-medium">{p.title}</div>
                    <div className="text-ink-muted text-xs">SKU: {p.variants?.[0]?.sku ?? '—'}</div>
                  </td>
                  <td className="text-ink-muted px-4 py-3">{p.vendor?.storeName ?? '—'}</td>
                  <td className="text-ink-muted px-4 py-3">{p.category?.name ?? '—'}</td>
                  <td className="px-4 py-3">{p.variants?.length ?? 0}</td>
                  <td className="px-4 py-3">
                    <Badge tone={PRODUCT_STATUS_TONE[p.status] ?? 'neutral'}>
                      {p.status.replace(/_/g, ' ')}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {p.status === 'PENDING_APPROVAL' ? (
                      <button
                        type="button"
                        onClick={() => setReviewing(p)}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-amber-50 px-3 py-1.5 text-[12px] font-bold text-amber-800 ring-1 ring-inset ring-amber-200 transition-all hover:bg-amber-100 hover:ring-amber-300"
                      >
                        Review
                        <ChevronRightIcon className="h-3.5 w-3.5" strokeWidth={2.5} />
                      </button>
                    ) : (
                      <span className="text-ink-subtle text-[12px] font-medium">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Review drawer */}
      <AnimatePresence>
        {reviewing && (
          <ProductReviewDrawer
            product={reviewing}
            onClose={() => setReviewing(null)}
            onSuccess={onReviewSuccess}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// ORDERS
// ══════════════════════════════════════════════════════════════════

const ORDER_STATUS_TONE: Record<string, BadgeTone> = {
  PENDING_PAYMENT: 'warning',
  PLACED: 'success',
  PARTIALLY_FULFILLED: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'danger',
};

const SUB_ORDER_STATUS_TONE: Record<string, BadgeTone> = {
  PENDING_PAYMENT: 'warning',
  PLACED: 'warning',
  CONFIRMED: 'success',
  PACKED: 'success',
  SHIPPED: 'success',
  DELIVERED: 'success',
  CLOSED: 'neutral',
  CANCELLED: 'danger',
  RETURN_REQUESTED: 'warning',
  RETURN_APPROVED: 'warning',
  RETURN_RECEIVED: 'warning',
  REFUNDED: 'neutral',
};

const toSentenceCase = (str: string) => {
  if (!str) return str;
  const s = str.replace(/_/g, ' ').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
};

// ══════════════════════════════════════════════════════════════════
// SETTLEMENTS  (Phase 2.2 — runs, payouts, per-vendor lookup)
// ══════════════════════════════════════════════════════════════════

const SETTLEMENT_STATUS_TONE: Record<string, BadgeTone> = {
  PENDING_REFUND_WINDOW: 'warning',
  ELIGIBLE: 'success',
  SETTLED: 'success',
  HELD: 'warning',
  REVERSED: 'danger',
};
const PAYOUT_STATUS_TONE: Record<string, BadgeTone> = {
  PENDING: 'warning',
  APPROVED: 'success',
  RELEASED: 'success',
  REJECTED: 'danger',
  FAILED: 'danger',
};

/**
 * Finance → Payouts now lives in `pages/payouts/`, split into sections.
 * Re-exported here so every existing import keeps working.
 */
export { PayoutsPage } from './payouts';

const BUCKETS = ['CASHBACK', 'REFUND', 'REFERRAL', 'PROMO', 'GENERIC'] as const;
const RESERVE_TYPES = ['REFUND_RISK', 'CHARGEBACK', 'FRAUD_FREEZE'] as const;

export function WalletPage() {
  const qc = useQueryClient();
  const [creditForm, setCreditForm] = useState({
    userId: '',
    bucket: 'PROMO' as (typeof BUCKETS)[number],
    amountRupees: '',
    note: '',
  });
  const [reserveForm, setReserveForm] = useState({
    vendorId: '',
    type: 'REFUND_RISK' as (typeof RESERVE_TYPES)[number],
    amountRupees: '',
    reason: '',
  });
  const [lookupUserId, setLookupUserId] = useState('');
  const [activeUser, setActiveUser] = useState('');
  const [lookupVendorId, setLookupVendorId] = useState('');
  const [activeVendorWallet, setActiveVendorWallet] = useState('');

  const customer = useQuery({
    queryKey: ['admin-wallet-customer', activeUser],
    queryFn: () => api.wallet.adminGetCustomer(activeUser),
    enabled: !!activeUser,
  });
  const vendorWallet = useQuery({
    queryKey: ['admin-wallet-vendor', activeVendorWallet],
    queryFn: () => api.wallet.adminGetVendor(activeVendorWallet),
    enabled: !!activeVendorWallet,
  });

  const issueCredit = useMutation({
    mutationFn: () =>
      api.wallet.issueCredit({
        userId: creditForm.userId.trim(),
        bucket: creditForm.bucket,
        amountPaise: Math.round(Number(creditForm.amountRupees) * 100),
        ...(creditForm.note ? { note: creditForm.note } : {}),
      }),
    onSuccess: () => {
      setCreditForm({ userId: '', bucket: 'PROMO', amountRupees: '', note: '' });
      void qc.invalidateQueries({ queryKey: ['admin-wallet-customer'] });
    },
  });

  const createReserve = useMutation({
    mutationFn: () =>
      api.wallet.createReserve({
        vendorId: reserveForm.vendorId.trim(),
        type: reserveForm.type,
        amountPaise: Math.round(Number(reserveForm.amountRupees) * 100),
        ...(reserveForm.reason ? { reason: reserveForm.reason } : {}),
      }),
    onSuccess: () => {
      setReserveForm({ vendorId: '', type: 'REFUND_RISK', amountRupees: '', reason: '' });
      void qc.invalidateQueries({ queryKey: ['admin-wallet-vendor'] });
    },
  });

  const expireSweep = useMutation({
    mutationFn: () => api.wallet.expireSweep(),
    onSuccess: (data: any) => {
      alert(`Expired ${data.expiredCount} credits, reversed ${formatPaise(data.reversedPaise)}`);
      void qc.invalidateQueries({ queryKey: ['admin-wallet-customer'] });
    },
  });

  const revokeCredit = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.wallet.revokeCredit(id, reason),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-wallet-customer'] });
    },
  });

  const releaseReserve = useMutation({
    mutationFn: ({
      id,
      action,
      note,
    }: {
      id: string;
      action: 'RELEASE' | 'FORFEIT';
      note?: string | null;
    }) => {
      const payload: { action: 'RELEASE' | 'FORFEIT'; note?: string } = { action };
      if (note) payload.note = note;
      return api.wallet.releaseReserve(id, payload);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-wallet-vendor'] });
    },
  });

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <FinancePageHeader
        title="Wallet"
        purpose="Issue customer credits, manage vendor reserves, and resolve wallet disputes."
        actions={
          <button
            type="button"
            onClick={() => {
              if (confirm('Run expiry sweep? This will expire all mature credits.')) {
                expireSweep.mutate();
              }
            }}
            disabled={expireSweep.isPending}
            className="border-border/60 bg-surface text-ink hover:bg-surface-subtle flex h-9 items-center justify-center rounded-lg border px-3.5 text-[12.5px] font-bold shadow-sm transition-all disabled:opacity-60"
          >
            {expireSweep.isPending ? 'Running…' : 'Run expiry sweep'}
          </button>
        }
      />

      {/* Forms Section */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Issue Customer Credit */}
        <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
          <FinancePanelHeader title="Issue customer credit" />
          <div className="p-5">
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                if (creditForm.userId && Number(creditForm.amountRupees) > 0) issueCredit.mutate();
              }}
            >
              <div className="space-y-3">
                <AsyncUserSelect
                  label="User ID"
                  value={creditForm.userId}
                  onChange={(val) => setCreditForm((f) => ({ ...f, userId: val }))}
                />
                <div>
                  <label className="text-ink mb-1.5 block text-[13px] font-semibold">Bucket</label>
                  <select
                    className="border-border/60 bg-surface text-ink hover:border-ink/20 focus:border-ink/30 focus:ring-ink/5 w-full rounded-xl border px-4 py-2 text-[13px] outline-none transition-all focus:ring-4"
                    value={creditForm.bucket}
                    onChange={(e) =>
                      setCreditForm((f) => ({
                        ...f,
                        bucket: e.target.value as (typeof BUCKETS)[number],
                      }))
                    }
                  >
                    {BUCKETS.map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </select>
                </div>
                <Input
                  label="Amount (₹)"
                  type="number"
                  min="0"
                  step="0.01"
                  value={creditForm.amountRupees}
                  onChange={(e) => setCreditForm((f) => ({ ...f, amountRupees: e.target.value }))}
                  required
                />
                <Input
                  label="Note"
                  value={creditForm.note}
                  onChange={(e) => setCreditForm((f) => ({ ...f, note: e.target.value }))}
                />
              </div>
              <div className="flex items-center justify-between pt-2">
                <span className="text-[13px] font-medium text-emerald-600">
                  {issueCredit.isSuccess ? 'Credit issued.' : ''}
                </span>
                <button
                  type="submit"
                  disabled={issueCredit.isPending}
                  className="bg-ink hover:bg-ink/90 flex h-10 items-center justify-center rounded-xl px-5 text-[13px] font-bold text-white shadow-sm transition-all"
                >
                  Issue credit
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Create Vendor Reserve Hold */}
        <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
          <FinancePanelHeader title="Create vendor reserve hold" />
          <div className="p-5">
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                if (reserveForm.vendorId && Number(reserveForm.amountRupees) > 0)
                  createReserve.mutate();
              }}
            >
              <div className="space-y-3">
                <AsyncVendorSelect
                  label="Vendor ID"
                  value={reserveForm.vendorId}
                  onChange={(val) => setReserveForm((f) => ({ ...f, vendorId: val }))}
                />
                <div>
                  <label className="text-ink mb-1.5 block text-[13px] font-semibold">Type</label>
                  <select
                    className="border-border/60 bg-surface text-ink hover:border-ink/20 focus:border-ink/30 focus:ring-ink/5 w-full rounded-xl border px-4 py-2 text-[13px] outline-none transition-all focus:ring-4"
                    value={reserveForm.type}
                    onChange={(e) =>
                      setReserveForm((f) => ({
                        ...f,
                        type: e.target.value as (typeof RESERVE_TYPES)[number],
                      }))
                    }
                  >
                    {RESERVE_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                </div>
                <Input
                  label="Amount (₹)"
                  type="number"
                  min="0"
                  step="0.01"
                  value={reserveForm.amountRupees}
                  onChange={(e) => setReserveForm((f) => ({ ...f, amountRupees: e.target.value }))}
                  required
                />
                <Input
                  label="Reason"
                  value={reserveForm.reason}
                  onChange={(e) => setReserveForm((f) => ({ ...f, reason: e.target.value }))}
                />
              </div>
              <div className="flex items-center justify-between pt-2">
                <span className="text-[13px] font-medium text-emerald-600">
                  {createReserve.isSuccess ? 'Hold created.' : ''}
                </span>
                <button
                  type="submit"
                  disabled={createReserve.isPending}
                  className="bg-ink hover:bg-ink/90 flex h-10 items-center justify-center rounded-xl px-5 text-[13px] font-bold text-white shadow-sm transition-all"
                >
                  Create hold
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Lookups Section */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Customer Wallet Lookup */}
        <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
          <FinancePanelHeader title="Customer wallet lookup" />
          <div className="border-border/60 border-b p-5">
            <form
              className="flex gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                setActiveUser(lookupUserId.trim());
              }}
            >
              <div className="flex-1">
                <AsyncUserSelect
                  placeholder="Select User..."
                  value={lookupUserId}
                  onChange={(val) => setLookupUserId(val)}
                />
              </div>
              <button
                type="submit"
                className="bg-ink hover:bg-ink/90 flex h-[38px] items-center justify-center rounded-xl px-4 text-[13px] font-bold text-white shadow-sm transition-all"
              >
                Load
              </button>
            </form>
          </div>
          {activeUser && (
            <div className="p-5">
              {customer.isLoading ? (
                <div className="grid min-h-[100px] place-items-center">
                  <Spinner size="sm" />
                </div>
              ) : customer.data ? (
                <div className="space-y-6">
                  <div>
                    <p className="text-ink-subtle text-[11px] font-semibold uppercase tracking-wider">
                      Total spendable
                    </p>
                    <p className="text-ink mt-1 text-[24px] font-bold">
                      {formatPaise(customer.data.totalSpendablePaise)}
                    </p>
                    <div className="mt-4 space-y-2">
                      {customer.data.buckets.map((b) => (
                        <div
                          key={b.bucket}
                          className="flex items-center justify-between text-[13px]"
                        >
                          <span className="text-ink font-medium">{b.bucket}</span>
                          <span className="text-ink font-bold">
                            {formatPaise(b.spendablePaise)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {customer.data.recentCredits.length > 0 && (
                    <div className="border-border/60 border-t pt-4">
                      <p className="text-ink-subtle mb-3 text-[11px] font-semibold uppercase tracking-wider">
                        Recent credits
                      </p>
                      <ul className="space-y-2">
                        {customer.data.recentCredits.slice(0, 5).map((c) => (
                          <li key={c.id} className="flex items-center justify-between text-[12px]">
                            <div className="flex items-center gap-2">
                              <Badge tone="neutral">{c.bucket}</Badge>
                              <span className="text-ink font-semibold">
                                {formatPaise(c.remainingPaise)} / {formatPaise(c.amountPaise)}
                              </span>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-ink-subtle font-medium">{c.status}</span>
                              {c.status === 'ACTIVE' && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const reason = prompt('Reason for revocation:');
                                    if (reason) revokeCredit.mutate({ id: c.id, reason });
                                  }}
                                  className="text-[12px] font-bold text-red-600 hover:text-red-700 hover:underline"
                                >
                                  Revoke
                                </button>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="border-border/60 border-t pt-4">
                    <p className="text-ink-subtle mb-3 text-[11px] font-semibold uppercase tracking-wider">
                      Ledger Timeline
                    </p>
                    <WalletLedgerTable entityId={activeUser} type="customer" />
                  </div>
                </div>
              ) : (
                <p className="text-ink-muted text-[13px] font-medium">No data found.</p>
              )}
            </div>
          )}
        </div>

        {/* Vendor Wallet Lookup */}
        <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
          <FinancePanelHeader title="Vendor wallet lookup" />
          <div className="border-border/60 border-b p-5">
            <form
              className="flex gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                setActiveVendorWallet(lookupVendorId.trim());
              }}
            >
              <div className="flex-1">
                <AsyncVendorSelect
                  placeholder="Select Vendor..."
                  value={lookupVendorId}
                  onChange={(val) => setLookupVendorId(val)}
                />
              </div>
              <button
                type="submit"
                className="bg-ink hover:bg-ink/90 flex h-[38px] items-center justify-center rounded-xl px-4 text-[13px] font-bold text-white shadow-sm transition-all"
              >
                Load
              </button>
            </form>
          </div>
          {activeVendorWallet && (
            <div className="p-5">
              {vendorWallet.isLoading ? (
                <div className="grid min-h-[100px] place-items-center">
                  <Spinner size="sm" />
                </div>
              ) : vendorWallet.data ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="border-border/40 bg-surface-subtle/30 rounded-xl border p-4">
                      <p className="text-ink-subtle text-[11px] font-semibold uppercase tracking-wider">
                        Cash
                      </p>
                      <p className="text-ink mt-1 text-[16px] font-bold">
                        {formatPaise(vendorWallet.data.cashPaise)}
                      </p>
                    </div>
                    <div className="border-border/40 bg-surface-subtle/30 rounded-xl border p-4">
                      <p className="text-ink-subtle text-[11px] font-semibold uppercase tracking-wider">
                        Held
                      </p>
                      <p className="text-ink mt-1 text-[16px] font-bold">
                        {formatPaise(vendorWallet.data.heldPaise)}
                      </p>
                    </div>
                    <div className="border-border/40 rounded-xl border border-emerald-100 bg-emerald-50/50 p-4">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700">
                        Withdrawable
                      </p>
                      <p className="mt-1 text-[16px] font-bold text-emerald-600">
                        {formatPaise(vendorWallet.data.withdrawablePaise)}
                      </p>
                    </div>
                  </div>
                  {vendorWallet.data.holdsByType.length > 0 && (
                    <div className="border-border/60 border-t pt-4">
                      <p className="text-ink-subtle mb-3 text-[11px] font-semibold uppercase tracking-wider">
                        Holds by type
                      </p>
                      <ul className="space-y-2">
                        {vendorWallet.data.holdsByType.map((h) => (
                          <li
                            key={h.type}
                            className="flex items-center justify-between text-[13px]"
                          >
                            <span className="text-ink font-medium">
                              {h.type.replace(/_/g, ' ')}
                            </span>
                            <div className="flex items-center gap-3">
                              <span className="text-ink font-bold">{formatPaise(h.paise)}</span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {vendorWallet.data.activeHolds && vendorWallet.data.activeHolds.length > 0 && (
                    <div className="border-border/60 border-t pt-4">
                      <p className="text-ink-subtle mb-3 text-[11px] font-semibold uppercase tracking-wider">
                        Active Holds (Risk Approval)
                      </p>
                      <ul className="space-y-3">
                        {vendorWallet.data.activeHolds.map((h) => (
                          <li
                            key={h.id}
                            className="bg-surface-subtle border-border/40 rounded-xl border p-3"
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="flex items-center gap-2">
                                  <Badge tone="neutral">{h.type.replace(/_/g, ' ')}</Badge>
                                  <span className="text-ink font-bold">
                                    {formatPaise(h.amountPaise)}
                                  </span>
                                </div>
                                {h.reason && (
                                  <p className="text-ink-subtle mt-1 text-[12px]">{h.reason}</p>
                                )}
                                <p className="text-ink-muted mt-1 text-[11px]">
                                  Held on: {new Date(h.createdAt).toLocaleDateString()}
                                </p>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    const note = prompt('Note for release:');
                                    if (note !== null)
                                      releaseReserve.mutate({ id: h.id, action: 'RELEASE', note });
                                  }}
                                  className="rounded-lg px-3 py-1.5 text-[12px] font-bold text-emerald-600 transition-colors hover:bg-emerald-50"
                                >
                                  Release
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (confirm('Are you sure you want to forfeit these funds?')) {
                                      const note = prompt('Note for forfeit:');
                                      if (note !== null)
                                        releaseReserve.mutate({
                                          id: h.id,
                                          action: 'FORFEIT',
                                          note,
                                        });
                                    }
                                  }}
                                  className="rounded-lg px-3 py-1.5 text-[12px] font-bold text-red-600 transition-colors hover:bg-red-50"
                                >
                                  Forfeit
                                </button>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="border-border/60 border-t pt-4">
                    <p className="text-ink-subtle mb-3 text-[11px] font-semibold uppercase tracking-wider">
                      Ledger Timeline
                    </p>
                    <WalletLedgerTable entityId={vendorWallet.data.vendorId} type="vendor" />
                  </div>
                </div>
              ) : (
                <p className="text-ink-muted text-[13px] font-medium">No data found.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════════
// CRM  (Phase 2.3 — lifecycle events, customer metrics, automations)
// ══════════════════════════════════════════════════════════════════

const STAGE_TONE: Record<string, BadgeTone> = {
  NEW: 'neutral',
  ACTIVE: 'success',
  REPEAT: 'success',
  AT_RISK: 'warning',
  DORMANT: 'warning',
  CHURNED: 'danger',
};

function CrmSegmentsTab() {
  const qc = useQueryClient();
  const segments = useQuery({
    queryKey: ['crm-segments'],
    queryFn: () => api.crm.listSegments(),
  });

  const [form, setForm] = useState({ name: '', description: '', filters: '{}' });
  const createSegment = useMutation({
    mutationFn: () =>
      api.crm.createSegment({
        name: form.name,
        ...(form.description ? { description: form.description } : {}),
        filters: JSON.parse(form.filters || '{}'),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['crm-segments'] });
      setForm({ name: '', description: '', filters: '{}' });
    },
  });

  return (
    <div className="space-y-6">
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <FinancePanelHeader title="Create segment" />
        <div className="p-5">
          <form
            className="flex flex-col gap-4 sm:flex-row sm:items-end"
            onSubmit={(e) => {
              e.preventDefault();
              createSegment.mutate();
            }}
          >
            <div className="flex-1">
              <Input
                label="Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>
            <div className="flex-1">
              <Input
                label="Description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div className="flex-1">
              <Input
                label="Filters (JSON)"
                value={form.filters}
                onChange={(e) => setForm({ ...form, filters: e.target.value })}
              />
            </div>
            <button
              type="submit"
              disabled={createSegment.isPending}
              className="bg-ink hover:bg-ink/90 flex h-[38px] items-center justify-center rounded-xl px-5 text-[13px] font-bold text-white shadow-sm transition-all"
            >
              {createSegment.isPending ? 'Creating...' : 'Create'}
            </button>
          </form>
        </div>
      </div>

      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <FinancePanelHeader title="Segments" />
        {segments.isLoading ? (
          <div className="p-5">
            <Spinner size="sm" />
          </div>
        ) : (segments.data ?? []).length === 0 ? (
          <div className="p-5">
            <p className="text-ink-muted text-[13px] font-medium">No segments found.</p>
          </div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-border/60 bg-surface-subtle/30 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                <th className="px-5 py-4">Name</th>
                <th className="px-5 py-4">Description</th>
                <th className="px-5 py-4">Created At</th>
              </tr>
            </thead>
            <tbody className="divide-border/40 divide-y text-[13px]">
              {(segments.data ?? []).map((s) => (
                <tr key={s.id} className="hover:bg-surface-subtle/30 group transition-colors">
                  <td className="px-5 py-4 font-semibold">{s.name}</td>
                  <td className="text-ink-subtle px-5 py-4">{s.description || '—'}</td>
                  <td className="text-ink-subtle px-5 py-4">
                    {new Date(s.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function CrmCampaignsTab() {
  const qc = useQueryClient();
  const campaigns = useQuery({
    queryKey: ['crm-campaigns'],
    queryFn: () => api.crm.listCampaigns(),
  });
  const segments = useQuery({
    queryKey: ['crm-segments'],
    queryFn: () => api.crm.listSegments(),
  });

  const [form, setForm] = useState({ name: '', description: '', segmentId: '' });
  const createCampaign = useMutation({
    mutationFn: () =>
      api.crm.createCampaign({
        name: form.name,
        ...(form.description ? { description: form.description } : {}),
        ...(form.segmentId ? { segmentId: form.segmentId } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['crm-campaigns'] });
      setForm({ name: '', description: '', segmentId: '' });
    },
  });

  return (
    <div className="space-y-6">
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <FinancePanelHeader title="Create campaign" />
        <div className="p-5">
          <form
            className="flex flex-col gap-4 sm:flex-row sm:items-end"
            onSubmit={(e) => {
              e.preventDefault();
              createCampaign.mutate();
            }}
          >
            <div className="flex-1">
              <Input
                label="Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>
            <div className="flex-1">
              <Input
                label="Description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div className="flex-1">
              <label className="text-ink mb-1.5 block text-[13px] font-bold">Segment</label>
              <select
                value={form.segmentId}
                onChange={(e) => setForm({ ...form, segmentId: e.target.value })}
                className="border-border/60 bg-surface hover:border-border/80 text-ink focus:border-ink focus:ring-ink/20 flex h-[38px] w-full rounded-xl border px-3 text-[13px] shadow-sm transition-all focus:outline-none focus:ring-2"
              >
                <option value="">None</option>
                {(segments.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="submit"
              disabled={createCampaign.isPending}
              className="bg-ink hover:bg-ink/90 flex h-[38px] items-center justify-center rounded-xl px-5 text-[13px] font-bold text-white shadow-sm transition-all"
            >
              {createCampaign.isPending ? 'Creating...' : 'Create'}
            </button>
          </form>
        </div>
      </div>

      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <FinancePanelHeader title="Campaigns" />
        {campaigns.isLoading ? (
          <div className="p-5">
            <Spinner size="sm" />
          </div>
        ) : (campaigns.data ?? []).length === 0 ? (
          <div className="p-5">
            <p className="text-ink-muted text-[13px] font-medium">No campaigns found.</p>
          </div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-border/60 bg-surface-subtle/30 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                <th className="px-5 py-4">Name</th>
                <th className="px-5 py-4">Status</th>
                <th className="px-5 py-4">Segment</th>
                <th className="px-5 py-4">Created At</th>
              </tr>
            </thead>
            <tbody className="divide-border/40 divide-y text-[13px]">
              {(campaigns.data ?? []).map((c) => (
                <tr key={c.id} className="hover:bg-surface-subtle/30 group transition-colors">
                  <td className="px-5 py-4 font-semibold">{c.name}</td>
                  <td className="px-5 py-4">
                    <Badge tone={c.status === 'ACTIVE' ? 'success' : 'neutral'}>{c.status}</Badge>
                  </td>
                  <td className="text-ink-subtle px-5 py-4">{c.segmentId || '—'}</td>
                  <td className="text-ink-subtle px-5 py-4">
                    {new Date(c.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function CrmConsentTab() {
  const [userId, setUserId] = useState('');
  const [activeUser, setActiveUser] = useState('');
  const consents = useQuery({
    queryKey: ['crm-consents', activeUser],
    queryFn: () => api.crm.listConsents(activeUser),
    enabled: !!activeUser,
  });

  return (
    <div className="space-y-6">
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <FinancePanelHeader title="Lookup user consent" />
        <div className="p-5">
          <form
            className="flex items-center gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              setActiveUser(userId.trim());
            }}
          >
            <Input
              placeholder="User ID"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              className="h-10 w-96 rounded-xl font-mono text-[13px]"
            />
            <button
              type="submit"
              className="bg-ink hover:bg-ink/90 flex h-[38px] items-center justify-center rounded-xl px-4 text-[13px] font-bold text-white shadow-sm transition-all"
            >
              Lookup
            </button>
          </form>
        </div>
        {activeUser && (
          <div className="border-border/60 border-t">
            {consents.isLoading ? (
              <div className="p-5">
                <Spinner size="sm" />
              </div>
            ) : (consents.data ?? []).length === 0 ? (
              <div className="p-5">
                <p className="text-ink-muted text-[13px] font-medium">
                  No consent records found for this user.
                </p>
              </div>
            ) : (
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-border/60 bg-surface-subtle/30 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                    <th className="px-5 py-4">Channel</th>
                    <th className="px-5 py-4">Status</th>
                    <th className="px-5 py-4">Last Updated</th>
                  </tr>
                </thead>
                <tbody className="divide-border/40 divide-y text-[13px]">
                  {(consents.data ?? []).map((c) => (
                    <tr key={c.id} className="hover:bg-surface-subtle/30 group transition-colors">
                      <td className="px-5 py-4 font-semibold">{c.channel}</td>
                      <td className="px-5 py-4">
                        <Badge tone={c.status === 'OPT_IN' ? 'success' : 'danger'}>
                          {c.status}
                        </Badge>
                      </td>
                      <td className="text-ink-subtle px-5 py-4">
                        {new Date(c.updatedAt).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function CrmPage() {
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState<'overview' | 'segments' | 'campaigns' | 'consent'>(
    'overview',
  );
  const [userId, setUserId] = useState('');
  const [activeUser, setActiveUser] = useState('');
  const [scanForm, setScanForm] = useState({ thresholdMinutes: '120', maxDispatch: '500' });

  const lifecycle = useQuery({
    queryKey: ['crm-lifecycle', activeUser],
    queryFn: () => api.crm.customerLifecycle(activeUser),
    enabled: !!activeUser,
  });
  const events = useQuery({
    queryKey: ['crm-events', activeUser],
    queryFn: () => api.crm.listEvents(activeUser, { limit: 50 }),
    enabled: !!activeUser,
  });

  const runScan = useMutation({
    mutationFn: () =>
      api.crm.runAbandonedCartScan({
        thresholdMinutes: Number(scanForm.thresholdMinutes),
        maxDispatch: Number(scanForm.maxDispatch),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['crm-events'] }),
  });

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="border-border/60 bg-surface flex flex-col justify-between gap-4 rounded-2xl border p-5 shadow-sm sm:flex-row sm:items-center">
        <div>
          <h3 className="text-ink text-[16px] font-bold tracking-tight">CRM</h3>
          <p className="text-ink-subtle mt-0.5 text-[13px] font-medium">
            Customer lifecycle, behaviour events, and growth automations.
          </p>
        </div>
      </div>

      <div className="border-border/60 flex items-center gap-6 border-b px-1">
        {(['overview', 'segments', 'campaigns', 'consent'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`pb-3 text-[13px] font-bold tracking-wide transition-colors ${
              activeTab === tab
                ? 'text-ink border-ink border-b-2'
                : 'text-ink-muted hover:text-ink border-b-2 border-transparent'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {activeTab === 'segments' && <CrmSegmentsTab />}
      {activeTab === 'campaigns' && <CrmCampaignsTab />}
      {activeTab === 'consent' && <CrmConsentTab />}

      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Abandoned Cart Scan */}
          <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
            <div className="border-border/60 border-b px-5 py-4">
              <h4 className="text-ink text-[14px] font-bold">Abandoned cart scan</h4>
            </div>
            <div className="p-5">
              <form
                className="flex flex-col gap-4 sm:flex-row sm:items-end"
                onSubmit={(e) => {
                  e.preventDefault();
                  runScan.mutate();
                }}
              >
                <div className="flex-1">
                  <Input
                    label="Threshold (minutes)"
                    type="number"
                    min="15"
                    max="10080"
                    value={scanForm.thresholdMinutes}
                    onChange={(e) =>
                      setScanForm((f) => ({ ...f, thresholdMinutes: e.target.value }))
                    }
                  />
                </div>
                <div className="flex-1">
                  <Input
                    label="Max dispatch"
                    type="number"
                    min="1"
                    max="5000"
                    value={scanForm.maxDispatch}
                    onChange={(e) => setScanForm((f) => ({ ...f, maxDispatch: e.target.value }))}
                  />
                </div>
                <button
                  type="submit"
                  disabled={runScan.isPending}
                  className="bg-ink hover:bg-ink/90 flex h-[38px] items-center justify-center rounded-xl px-5 text-[13px] font-bold text-white shadow-sm transition-all"
                >
                  {runScan.isPending ? 'Running...' : 'Run scan'}
                </button>
              </form>
              {runScan.data && (
                <div className="border-border/60 bg-surface-subtle/30 mt-5 grid grid-cols-3 gap-4 rounded-xl border p-4">
                  <div>
                    <p className="text-ink-subtle text-[11px] font-semibold uppercase tracking-wider">
                      Scanned
                    </p>
                    <p className="text-ink mt-1 text-[16px] font-bold">
                      {runScan.data.scannedCount}
                    </p>
                  </div>
                  <div>
                    <p className="text-ink-subtle text-[11px] font-semibold uppercase tracking-wider">
                      Abandoned
                    </p>
                    <p className="text-ink mt-1 text-[16px] font-bold">
                      {runScan.data.abandonedCount}
                    </p>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700">
                      Nudges dispatched
                    </p>
                    <p className="mt-1 text-[16px] font-bold text-emerald-600">
                      {runScan.data.nudgesDispatched}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Customer Lookup */}
          <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
            <div className="border-border/60 border-b px-5 py-4">
              <h4 className="text-ink text-[14px] font-bold">Customer lookup</h4>
            </div>
            <div className="border-border/60 border-b p-5">
              <form
                className="flex items-center gap-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  setActiveUser(userId.trim());
                }}
              >
                <Input
                  placeholder="User ID"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  className="h-10 w-96 rounded-xl font-mono text-[13px]"
                />
                <button
                  type="submit"
                  className="bg-ink hover:bg-ink/90 flex h-[38px] items-center justify-center rounded-xl px-4 text-[13px] font-bold text-white shadow-sm transition-all"
                >
                  Load
                </button>
              </form>
            </div>

            {activeUser && (
              <div className="space-y-8 p-5">
                <div>
                  <h4 className="text-ink mb-4 text-[14px] font-bold">Lifecycle metrics</h4>
                  {lifecycle.isLoading ? (
                    <Spinner size="sm" />
                  ) : lifecycle.data ? (
                    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                      <div className="border-border/40 bg-surface-subtle/30 rounded-xl border p-4">
                        <p className="text-ink-subtle text-[11px] font-semibold uppercase tracking-wider">
                          Stage
                        </p>
                        <div className="mt-1.5">
                          <Badge tone={STAGE_TONE[lifecycle.data.stage] ?? 'neutral'}>
                            {lifecycle.data.stage}
                          </Badge>
                        </div>
                      </div>
                      <div className="border-border/40 bg-surface-subtle/30 rounded-xl border p-4">
                        <p className="text-ink-subtle text-[11px] font-semibold uppercase tracking-wider">
                          LTV
                        </p>
                        <p className="text-ink mt-1 text-[16px] font-bold">
                          {formatPaise(lifecycle.data.lifetimeValuePaise)}
                        </p>
                      </div>
                      <div className="border-border/40 bg-surface-subtle/30 rounded-xl border p-4">
                        <p className="text-ink-subtle text-[11px] font-semibold uppercase tracking-wider">
                          Orders
                        </p>
                        <p className="text-ink mt-1 text-[16px] font-bold">
                          {lifecycle.data.orderCount}
                        </p>
                      </div>
                      <div className="border-border/40 bg-surface-subtle/30 rounded-xl border p-4">
                        <p className="text-ink-subtle text-[11px] font-semibold uppercase tracking-wider">
                          AOV
                        </p>
                        <p className="text-ink mt-1 text-[16px] font-bold">
                          {formatPaise(lifecycle.data.avgOrderValuePaise)}
                        </p>
                      </div>
                      <div className="border-border/40 bg-surface-subtle/30 rounded-xl border p-4">
                        <p className="text-ink-subtle text-[11px] font-semibold uppercase tracking-wider">
                          First order
                        </p>
                        <p className="text-ink mt-1 text-[13px] font-medium">
                          {lifecycle.data.firstOrderAt
                            ? new Date(lifecycle.data.firstOrderAt).toLocaleDateString()
                            : '—'}
                        </p>
                      </div>
                      <div className="border-border/40 bg-surface-subtle/30 rounded-xl border p-4">
                        <p className="text-ink-subtle text-[11px] font-semibold uppercase tracking-wider">
                          Last order
                        </p>
                        <p className="text-ink mt-1 text-[13px] font-medium">
                          {lifecycle.data.lastOrderAt
                            ? new Date(lifecycle.data.lastOrderAt).toLocaleDateString()
                            : '—'}
                        </p>
                      </div>
                      <div className="border-border/40 bg-surface-subtle/30 rounded-xl border p-4">
                        <p className="text-ink-subtle text-[11px] font-semibold uppercase tracking-wider">
                          Last seen
                        </p>
                        <p className="text-ink mt-1 text-[13px] font-medium">
                          {lifecycle.data.lastSeenAt
                            ? new Date(lifecycle.data.lastSeenAt).toLocaleDateString()
                            : '—'}
                        </p>
                      </div>
                      <div className="border-border/40 bg-surface-subtle/30 rounded-xl border p-4">
                        <p className="text-ink-subtle text-[11px] font-semibold uppercase tracking-wider">
                          Days since last order
                        </p>
                        <p className="text-ink mt-1 text-[16px] font-bold">
                          {lifecycle.data.daysSinceLastOrder ?? '—'}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-ink-muted text-[13px] font-medium">
                      No lifecycle data found.
                    </p>
                  )}
                </div>

                <div>
                  <h4 className="text-ink mb-4 text-[14px] font-bold">Recent events</h4>
                  {events.isLoading ? (
                    <Spinner size="sm" />
                  ) : (events.data ?? []).length === 0 ? (
                    <p className="text-ink-muted text-[13px] font-medium">No events recorded.</p>
                  ) : (
                    <div className="border-border/60 bg-surface overflow-hidden rounded-xl border shadow-sm">
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                          <thead>
                            <tr className="border-border/60 bg-surface-subtle/30 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                              <th className="px-5 py-4">When</th>
                              <th className="px-5 py-4">Source</th>
                              <th className="px-5 py-4">Kind</th>
                              <th className="px-5 py-4">Ref</th>
                            </tr>
                          </thead>
                          <tbody className="divide-border/40 divide-y text-[13px]">
                            {(events.data ?? []).map((ev: any) => (
                              <tr
                                key={ev.id}
                                className="hover:bg-surface-subtle/30 group transition-colors"
                              >
                                <td className="text-ink-subtle px-5 py-4 text-xs">
                                  {new Date(ev.createdAt).toLocaleString()}
                                </td>
                                <td className="px-5 py-4">
                                  <Badge tone="neutral">{ev.source}</Badge>
                                </td>
                                <td className="px-5 py-4 font-mono text-xs">{ev.kind}</td>
                                <td className="text-ink-subtle px-5 py-4 text-xs">
                                  {ev.refType ? `${ev.refType}:${ev.refId ?? ''}` : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// NOTIFICATIONS  (Phase 2.3 — templates, send, logs)
// ══════════════════════════════════════════════════════════════════

const CHANNELS: NotificationChannel[] = ['EMAIL', 'SMS', 'WHATSAPP', 'IN_APP'];
const NOTIF_STATUS_TONE: Record<string, BadgeTone> = {
  QUEUED: 'warning',
  SENT: 'success',
  DELIVERED: 'success',
  FAILED: 'danger',
};

export function NotificationsPage() {
  const qc = useQueryClient();
  const templates = useQuery({
    queryKey: ['notif-templates'],
    queryFn: () => api.notifications.listTemplates(),
  });
  const logs = useQuery({
    queryKey: ['notif-logs'],
    queryFn: () => api.notifications.listLogs({ limit: 100 }),
  });

  const [tplForm, setTplForm] = useState({
    key: '',
    channel: 'EMAIL' as NotificationChannel,
    locale: 'en-IN',
    subject: '',
    body: '',
    isActive: true,
  });
  const [sendForm, setSendForm] = useState({
    templateKey: '',
    channel: 'EMAIL' as NotificationChannel,
    to: '',
    vars: '',
  });

  const upsert = useMutation({
    mutationFn: () =>
      api.notifications.upsertTemplate({
        key: tplForm.key.trim(),
        channel: tplForm.channel,
        locale: tplForm.locale,
        ...(tplForm.subject ? { subject: tplForm.subject } : {}),
        body: tplForm.body,
        isActive: tplForm.isActive,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notif-templates'] }),
  });

  const send = useMutation({
    mutationFn: () => {
      const body: Parameters<typeof api.notifications.send>[0] = {
        templateKey: sendForm.templateKey.trim(),
        channel: sendForm.channel,
        to: sendForm.to.trim(),
      };
      if (sendForm.vars.trim()) {
        try {
          body.vars = JSON.parse(sendForm.vars);
        } catch {
          throw new Error('vars must be valid JSON');
        }
      }
      return api.notifications.send(body);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notif-logs'] }),
  });

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="border-border/60 bg-surface flex flex-col justify-between gap-4 rounded-2xl border p-5 shadow-sm sm:flex-row sm:items-center">
        <div>
          <h3 className="text-ink text-[16px] font-bold tracking-tight">Notifications</h3>
          <p className="text-ink-subtle mt-0.5 text-[13px] font-medium">
            Templates, manual sends, and delivery logs across email/SMS/WhatsApp/in-app.
          </p>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Upsert Template */}
        <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
          <div className="border-border/60 border-b px-5 py-4">
            <h4 className="text-ink text-[14px] font-bold">Upsert template</h4>
          </div>
          <div className="p-5">
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                if (tplForm.key && tplForm.body) upsert.mutate();
              }}
            >
              <Input
                label="Key"
                value={tplForm.key}
                onChange={(e) => setTplForm((f) => ({ ...f, key: e.target.value }))}
                hint="e.g. order.placed, otp.login"
                required
              />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-ink mb-1.5 block text-[13px] font-semibold">Channel</label>
                  <select
                    className="border-border/60 focus:border-ink focus:ring-ink/20 bg-surface h-10 w-full rounded-xl border px-3 text-[13px] outline-none transition-all focus:ring-4"
                    value={tplForm.channel}
                    onChange={(e) =>
                      setTplForm((f) => ({ ...f, channel: e.target.value as NotificationChannel }))
                    }
                  >
                    {CHANNELS.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                <Input
                  label="Locale"
                  value={tplForm.locale}
                  onChange={(e) => setTplForm((f) => ({ ...f, locale: e.target.value }))}
                />
              </div>
              <Input
                label="Subject (email only)"
                value={tplForm.subject}
                onChange={(e) => setTplForm((f) => ({ ...f, subject: e.target.value }))}
              />
              <div>
                <label className="text-ink mb-1.5 block text-[13px] font-semibold">Body</label>
                <textarea
                  className="border-border/60 focus:border-ink focus:ring-ink/20 bg-surface w-full rounded-xl border p-3 font-mono text-[13px] outline-none transition-all focus:ring-4"
                  rows={4}
                  value={tplForm.body}
                  onChange={(e) => setTplForm((f) => ({ ...f, body: e.target.value }))}
                  placeholder="Hi {{name}}, your order {{orderNumber}} is confirmed."
                  required
                />
              </div>
              <label className="text-ink flex items-center gap-2 text-[13px] font-medium">
                <input
                  type="checkbox"
                  className="border-border/60 text-ink focus:ring-ink/20 h-4 w-4 rounded border transition-all focus:ring-2"
                  checked={tplForm.isActive}
                  onChange={(e) => setTplForm((f) => ({ ...f, isActive: e.target.checked }))}
                />
                Active
              </label>
              <div className="border-border/60 flex items-center justify-between border-t pt-4">
                {upsert.isSuccess ? (
                  <p className="text-[13px] font-medium text-emerald-600">Template saved.</p>
                ) : (
                  <div />
                )}
                <button
                  type="submit"
                  disabled={upsert.isPending}
                  className="bg-ink hover:bg-ink/90 flex h-[38px] items-center justify-center rounded-xl px-5 text-[13px] font-bold text-white shadow-sm transition-all disabled:opacity-50"
                >
                  {upsert.isPending ? 'Saving...' : 'Save template'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Send Notification */}
        <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
          <div className="border-border/60 border-b px-5 py-4">
            <h4 className="text-ink text-[14px] font-bold">Send notification</h4>
          </div>
          <div className="p-5">
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                if (sendForm.templateKey && sendForm.to) send.mutate();
              }}
            >
              <Input
                label="Template key"
                value={sendForm.templateKey}
                onChange={(e) => setSendForm((f) => ({ ...f, templateKey: e.target.value }))}
                required
              />
              <div>
                <label className="text-ink mb-1.5 block text-[13px] font-semibold">Channel</label>
                <select
                  className="border-border/60 focus:border-ink focus:ring-ink/20 bg-surface h-10 w-full rounded-xl border px-3 text-[13px] outline-none transition-all focus:ring-4"
                  value={sendForm.channel}
                  onChange={(e) =>
                    setSendForm((f) => ({ ...f, channel: e.target.value as NotificationChannel }))
                  }
                >
                  {CHANNELS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <Input
                label="To"
                value={sendForm.to}
                onChange={(e) => setSendForm((f) => ({ ...f, to: e.target.value }))}
                hint="email, phone or userId"
                required
              />
              <div>
                <label className="text-ink mb-1.5 block text-[13px] font-semibold">
                  Vars (JSON)
                </label>
                <textarea
                  className="border-border/60 focus:border-ink focus:ring-ink/20 bg-surface w-full rounded-xl border p-3 font-mono text-[13px] outline-none transition-all focus:ring-4"
                  rows={2}
                  value={sendForm.vars}
                  onChange={(e) => setSendForm((f) => ({ ...f, vars: e.target.value }))}
                  placeholder='{"name":"Asha","orderNumber":"TS-001"}'
                />
              </div>
              <div className="border-border/60 flex items-center justify-between border-t pt-4">
                <div>
                  {send.isSuccess && (
                    <p className="text-[13px] font-medium text-emerald-600">Queued.</p>
                  )}
                  {send.isError && (
                    <p className="text-[13px] font-medium text-red-600">
                      {send.error instanceof Error ? send.error.message : 'Send failed'}
                    </p>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={send.isPending}
                  className="bg-ink hover:bg-ink/90 flex h-[38px] items-center justify-center rounded-xl px-5 text-[13px] font-bold text-white shadow-sm transition-all disabled:opacity-50"
                >
                  {send.isPending ? 'Sending...' : 'Send'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Templates List */}
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="border-border/60 border-b px-5 py-4">
          <h4 className="text-ink text-[14px] font-bold">Templates</h4>
        </div>
        <div>
          {templates.isLoading ? (
            <div className="flex justify-center p-8">
              <Spinner size="sm" />
            </div>
          ) : (templates.data ?? []).length === 0 ? (
            <div className="p-8">
              <EmptyState
                title="No templates yet"
                description="Create your first notification template above."
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-border/60 bg-surface-subtle/30 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                    <th className="px-5 py-4">Key</th>
                    <th className="px-5 py-4">Channel</th>
                    <th className="px-5 py-4">Locale</th>
                    <th className="px-5 py-4">Subject</th>
                    <th className="px-5 py-4">Active</th>
                    <th className="px-5 py-4">Updated</th>
                  </tr>
                </thead>
                <tbody className="divide-border/40 divide-y text-[13px]">
                  {(templates.data ?? []).map((t: NotificationTemplate) => (
                    <tr key={t.id} className="hover:bg-surface-subtle/30 group transition-colors">
                      <td className="px-5 py-4 font-mono text-xs">{t.key}</td>
                      <td className="px-5 py-4">
                        <Badge tone="neutral">{t.channel}</Badge>
                      </td>
                      <td className="text-ink-subtle px-5 py-4">{t.locale}</td>
                      <td className="text-ink-subtle px-5 py-4">{t.subject ?? '—'}</td>
                      <td className="px-5 py-4">
                        {t.isActive ? (
                          <Badge tone="success">On</Badge>
                        ) : (
                          <Badge tone="neutral">Off</Badge>
                        )}
                      </td>
                      <td className="text-ink-subtle px-5 py-4 text-xs">
                        {new Date(t.updatedAt).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Recent Logs List */}
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="border-border/60 border-b px-5 py-4">
          <h4 className="text-ink text-[14px] font-bold">Recent logs</h4>
        </div>
        <div>
          {logs.isLoading ? (
            <div className="flex justify-center p-8">
              <Spinner size="sm" />
            </div>
          ) : (logs.data ?? []).length === 0 ? (
            <p className="text-ink-muted p-5 text-[13px] font-medium">No deliveries yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-border/60 bg-surface-subtle/30 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                    <th className="px-5 py-4">When</th>
                    <th className="px-5 py-4">Template</th>
                    <th className="px-5 py-4">Channel</th>
                    <th className="px-5 py-4">To</th>
                    <th className="px-5 py-4">Status</th>
                    <th className="px-5 py-4">Error</th>
                  </tr>
                </thead>
                <tbody className="divide-border/40 divide-y text-[13px]">
                  {(logs.data ?? []).map((l: NotificationLog) => (
                    <tr key={l.id} className="hover:bg-surface-subtle/30 group transition-colors">
                      <td className="text-ink-subtle px-5 py-4 text-xs">
                        {new Date(l.createdAt).toLocaleString()}
                      </td>
                      <td className="px-5 py-4 font-mono text-xs">{l.templateKey}</td>
                      <td className="px-5 py-4">
                        <Badge tone="neutral">{l.channel}</Badge>
                      </td>
                      <td className="text-ink-subtle px-5 py-4 text-xs">{l.toAddress}</td>
                      <td className="px-5 py-4">
                        <Badge tone={NOTIF_STATUS_TONE[l.status] ?? 'neutral'}>{l.status}</Badge>
                      </td>
                      <td className="px-5 py-4 text-xs text-red-600">{l.error ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// NOT FOUND
// ══════════════════════════════════════════════════════════════════

export const NotFoundPage = () => (
  <EmptyState title="Page not found" description="That admin page doesn't exist." />
);
